import { useCallback, useEffect, useMemo, useState } from 'react';
import { STAGES, STAGE_LABEL, localDate, FACTORY_TZ, type AreaDto, type AttrSetting, type EquipmentDto, type ProdCardDto, type ProductDto, type Stage } from '@app/shared';
import { api } from '../api';
import { platform } from '../platform';
import { useApp } from '../state';
import { Icon, Sheet } from '../ui';
import { areaPath } from './Panels';
import { ProductFacts } from './Products';
import { StepContent, StepList } from './Steps';
import type { EquipmentFileDto } from '@app/shared';

/* ---------- Фильтры и группировка по любым характеристикам ---------- */

/** Условие фильтра: текст «содержит», число «точно» или «от–до». */
export interface Cond {
  key: string;
  text?: string;
  eq?: string;
  from?: string;
  to?: string;
}
const NAME = '__name';
const QTY = '__qty';
const WHO = '__who';
const label = (k: string) => (k === NAME ? 'Наименование / артикул' : k === QTY ? 'Количество' : k === WHO ? 'Исполнитель' : k);

const toNum = (v: string | number | null | undefined) => {
  if (v === null || v === undefined) return NaN;
  const m = /-?\d+(?:[.,]\d+)?/.exec(String(v).replace(/\s/g, ''));
  return m ? Number(m[0].replace(',', '.')) : NaN;
};
function valueOf(c: ProdCardDto, key: string): string {
  if (key === NAME) return `${c.sku ?? ''} ${c.name ?? c.title}`;
  if (key === QTY) return c.qty === null ? '' : String(c.qty);
  if (key === WHO) return c.assignees.map((a) => a.name).join(', ');
  return c.attrs[key] ?? '';
}
function matches(c: ProdCardDto, f: Cond): boolean {
  const v = valueOf(c, f.key);
  if (f.text?.trim()) return v.toLowerCase().includes(f.text.trim().toLowerCase());
  const n = toNum(v);
  if (f.eq?.trim()) return n === toNum(f.eq);
  if (f.from?.trim() && !(n >= toNum(f.from))) return false;
  if (f.to?.trim() && !(n <= toNum(f.to))) return false;
  return true;
}
function load<T>(key: string, def: T): T {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : def;
  } catch {
    return def;
  }
}
function save(key: string, v: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* нет хранилища */
  }
}

/**
 * Доска производства: столбцы — этапы (План месяца → … → Отгрузка), строки — группировка по выбранной
 * характеристике (например, по упаковке или диаметру). Фильтры — по любым характеристикам, количеству, исполнителю.
 */
export function ProdBoard() {
  const { fail, me } = useApp();
  const [cards, setCards] = useState<ProdCardDto[] | null>(null);
  const [attrs, setAttrs] = useState<AttrSetting[]>([]);
  const [areas, setAreas] = useState<AreaDto[]>([]);
  const [filters, setFilters] = useState<Cond[]>(() => load('board.filters', []));
  const [group, setGroup] = useState<string>(() => load('board.group', ''));
  const [mine, setMine] = useState<boolean>(() => load('board.mine', !me.manager));
  const [openFilter, setOpenFilter] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const [c, a, ar] = await Promise.all([api.cards(), api.attrs(), api.areas()]);
      setCards(c);
      setAttrs(a);
      setAreas(ar);
    } catch (e) {
      fail(e);
    }
  }, [fail]);
  useEffect(() => {
    void reload();
    const t = setInterval(() => void reload(), 30_000);
    return () => clearInterval(t);
  }, [reload]);

  const shown = useMemo(
    () => (cards ?? []).filter((c) => (!mine || c.assignees.some((a) => a.id === me.employee?.id)) && filters.every((f) => matches(c, f))),
    [cards, filters, mine, me.employee?.id],
  );
  const groups = useMemo(() => {
    if (!group) return [['', shown]] as [string, ProdCardDto[]][];
    const m = new Map<string, ProdCardDto[]>();
    for (const c of shown) {
      const k = valueOf(c, group) || '—';
      m.set(k, [...(m.get(k) ?? []), c]);
    }
    return [...m.entries()].sort(([a], [b]) => {
      const na = toNum(a);
      const nb = toNum(b);
      return Number.isFinite(na) && Number.isFinite(nb) ? na - nb : a.localeCompare(b, 'ru');
    });
  }, [shown, group]);
  const showAttrs = attrs.filter((a) => a.show).map((a) => a.key);

  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: 8 }}>
        <div className="chips" role="group" aria-label="Чьи карточки">
          <button type="button" className="chip" aria-pressed={mine} onClick={() => (setMine(true), save('board.mine', true))}>
            Мои
          </button>
          <button type="button" className="chip" aria-pressed={!mine} onClick={() => (setMine(false), save('board.mine', false))}>
            Все
          </button>
        </div>
        <span className="grow" />
        <button type="button" className={`btn btn-sm ${filters.length || group ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setOpenFilter(true)}>
          <Icon name="search" size={16} />
          Фильтр{filters.length ? ` · ${filters.length}` : ''}
          {group ? ' · группы' : ''}
        </button>
      </div>
      {(filters.length > 0 || group) && (
        <div className="chips">
          {filters.map((f, i) => (
            <button key={i} type="button" className="chip" aria-pressed onClick={() => (setFilters(filters.filter((_, j) => j !== i)), save('board.filters', filters.filter((_, j) => j !== i)))}>
              {label(f.key)}: {f.text ? `«${f.text}»` : f.eq ? `= ${f.eq}` : `${f.from || '…'}–${f.to || '…'}`} ✕
            </button>
          ))}
          {group && (
            <button type="button" className="chip" aria-pressed onClick={() => (setGroup(''), save('board.group', ''))}>
              Группы: {label(group)} ✕
            </button>
          )}
        </div>
      )}

      {!cards && <div className="skeleton" />}
      {cards && (
        <div className="stage-board" role="table" aria-label="Доска этапов">
          <div className="stage-row stage-head" role="row">
            {STAGES.map((s) => (
              <div key={s} className="stage-col" role="columnheader">
                {STAGE_LABEL[s]} <b className="col-count">{shown.filter((c) => c.stage === s).length}</b>
              </div>
            ))}
          </div>
          {groups.map(([g, list]) => (
            <div key={g || 'all'} role="rowgroup">
              {group && (
                <div className="stage-group">
                  {label(group)}: <b>{g}</b> · {list.length}
                </div>
              )}
              <div className="stage-row" role="row">
                {STAGES.map((s) => (
                  <div key={s} className="stage-col" role="cell">
                    {list
                      .filter((c) => c.stage === s)
                      .map((c) => (
                        <Card key={c.id} c={c} attrs={showAttrs.filter((k) => k !== group)} onOpen={() => setOpen(c.id)} />
                      ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {cards && cards.length === 0 && (
        <p className="muted small" style={{ textAlign: 'center' }}>
          Карточек нет. Загрузите план производства: «Ещё → Загрузка таблиц».
        </p>
      )}

      {openFilter && (
        <FilterSheet
          attrs={attrs.map((a) => a.key)}
          cards={cards ?? []}
          filters={filters}
          group={group}
          onClose={() => setOpenFilter(false)}
          onApply={(f, g) => {
            setFilters(f);
            setGroup(g);
            save('board.filters', f);
            save('board.group', g);
            setOpenFilter(false);
          }}
        />
      )}
      {open && <CardSheet id={open} areas={areas} onClose={() => setOpen(null)} onChanged={reload} />}
    </div>
  );
}

function Card({ c, attrs, onOpen }: { c: ProdCardDto; attrs: string[]; onOpen: () => void }) {
  const share = c.qty ? Math.min(1, c.done / c.qty) : 0;
  return (
    <button type="button" className="pcard" onClick={onOpen}>
      <span className="pcard-sku">{c.sku ?? c.title}</span>
      {c.name && c.name !== c.sku && <span className="pcard-name">{c.name}</span>}
      {c.qty !== null && (
        <span className="pcard-qty">
          <span>
            {c.done.toLocaleString('ru-RU')} / {c.qty.toLocaleString('ru-RU')} шт.
          </span>
          <span className="progress">
            <i style={{ width: `${Math.round(share * 100)}%` }} />
          </span>
        </span>
      )}
      {attrs.some((k) => c.attrs[k]) && (
        <span className="pcard-attrs">
          {attrs
            .filter((k) => c.attrs[k])
            .map((k) => (
              <span key={k}>
                {k}: <b>{c.attrs[k]}</b>
              </span>
            ))}
        </span>
      )}
      {(c.assignees.length > 0 || c.shiftLabel) && (
        <span className="pcard-who">
          {c.shiftLabel ? `${c.shiftLabel} · ` : ''}
          {c.assignees.map((a) => a.name.split(' ')[0]).join(', ')}
        </span>
      )}
      {c.stage === 'changeover' && !c.hasChangeover && <span className="pcard-note">Описание переналадки скоро появится</span>}
    </button>
  );
}

function FilterSheet({
  attrs,
  cards,
  filters,
  group,
  onClose,
  onApply,
}: {
  attrs: string[];
  cards: ProdCardDto[];
  filters: Cond[];
  group: string;
  onClose: () => void;
  onApply: (f: Cond[], g: string) => void;
}) {
  const [list, setList] = useState<Cond[]>(filters);
  const [g, setG] = useState(group);
  const keys = [NAME, QTY, WHO, ...attrs];
  // Числовая характеристика — если почти все значения числа: тогда «точно» или «от–до».
  const numeric = (k: string) => {
    const vals = cards.map((c) => valueOf(c, k)).filter(Boolean);
    return k === QTY || (vals.length > 0 && vals.filter((v) => Number.isFinite(toNum(v)) && /^\s*-?[\d\s.,]+\s*(мм|шт|кг|г)?\s*$/i.test(v)).length >= vals.length * 0.8);
  };
  const set = (i: number, patch: Partial<Cond>) => setList((l) => l.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <Sheet title="Найти и сгруппировать" onClose={onClose}>
      {list.map((f, i) => (
        <div key={i} className="proc-edit" style={{ gap: 6 }}>
          <div className="row" style={{ gap: 6 }}>
            <select className="input grow" aria-label={`Условие ${i + 1}`} value={f.key} onChange={(e) => set(i, { key: e.target.value, text: '', eq: '', from: '', to: '' })}>
              {keys.map((k) => (
                <option key={k} value={k}>
                  {label(k)}
                </option>
              ))}
            </select>
            <button type="button" className="icon-btn" aria-label="Убрать условие" onClick={() => setList((l) => l.filter((_, j) => j !== i))}>
              <Icon name="trash" size={16} />
            </button>
          </div>
          {numeric(f.key) ? (
            <div className="row" style={{ gap: 6 }}>
              <input className="input" inputMode="decimal" placeholder="точно" aria-label="Точно" value={f.eq ?? ''} onChange={(e) => set(i, { eq: e.target.value })} />
              <span className="small muted">или</span>
              <input className="input" inputMode="decimal" placeholder="от" aria-label="От" value={f.from ?? ''} onChange={(e) => set(i, { from: e.target.value, eq: '' })} />
              <input className="input" inputMode="decimal" placeholder="до" aria-label="До" value={f.to ?? ''} onChange={(e) => set(i, { to: e.target.value, eq: '' })} />
            </div>
          ) : (
            <input className="input" placeholder="содержит…" aria-label="Содержит" value={f.text ?? ''} onChange={(e) => set(i, { text: e.target.value })} />
          )}
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setList((l) => [...l, { key: NAME }])}>
        <Icon name="plus" size={16} />
        Условие
      </button>
      <div className="field">
        <label htmlFor="grp">Группировать строками по</label>
        <select id="grp" className="input" value={g} onChange={(e) => setG(e.target.value)}>
          <option value="">— без группировки</option>
          {[WHO, ...attrs].map((k) => (
            <option key={k} value={k}>
              {label(k)}
            </option>
          ))}
        </select>
      </div>
      <div className="grid-half">
        <button type="button" className="btn btn-ghost" onClick={() => onApply([], '')}>
          Сбросить
        </button>
        <button type="button" className="btn btn-primary" onClick={() => onApply(list.filter((f) => f.text?.trim() || f.eq?.trim() || f.from?.trim() || f.to?.trim()), g)}>
          Показать
        </button>
      </div>
    </Sheet>
  );
}

/** Карточка производства: характеристики, количество, места хранения, этап, назначение на смену, переналадка. */
function CardSheet({ id, areas, onClose, onChanged }: { id: string; areas: AreaDto[]; onClose: () => void; onChanged: () => Promise<void> }) {
  const { fail, toast } = useApp();
  const [c, setC] = useState<ProdCardDto | null>(null);
  const [p, setP] = useState<ProductDto | null>(null);
  const [assign, setAssign] = useState(false);
  const [step, setStep] = useState<{ n: number; text: string; details?: string | null; fileIds?: string[] } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const x = await api.card(id);
      setC(x);
      if (x.productId) setP(await api.product(x.productId));
    } catch (e) {
      fail(e);
      onClose();
    }
  }, [id, fail, onClose]);
  useEffect(() => void load(), [load]);

  async function move(stage: Stage) {
    setBusy(true);
    try {
      setC(await api.moveCard(id, stage));
      platform.haptic('light');
      toast(`→ ${STAGE_LABEL[stage]}`);
      await onChanged();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  if (!c) {
    return (
      <Sheet title="Карточка" onClose={onClose}>
        <div className="skeleton" />
      </Sheet>
    );
  }
  const i = STAGES.indexOf(c.stage);
  return (
    <Sheet title={c.sku ?? 'Карточка'} onClose={onClose}>
      <p className="step-title">{c.name ?? c.title}</p>
      <div className="stage-steps" aria-label="Этап">
        {STAGES.map((s, k) => (
          <span key={s} className={k < i ? 'past' : k === i ? 'now' : ''}>
            {STAGE_LABEL[s]}
          </span>
        ))}
      </div>
      {c.qty !== null && (
        <div className="card flat">
          <b>
            Сделано {c.done.toLocaleString('ru-RU')} из {c.qty.toLocaleString('ru-RU')} шт.
          </b>
          {c.done < c.qty && <span className="muted"> · осталось {(c.qty - c.done).toLocaleString('ru-RU')}</span>}
          <div className="progress" style={{ marginTop: 8 }}>
            <i style={{ width: `${Math.round(Math.min(1, c.done / c.qty) * 100)}%` }} />
          </div>
        </div>
      )}
      <dl className="facts">
        {c.period && (
          <div>
            <dt>План на месяц</dt>
            <dd>{c.period}</dd>
          </div>
        )}
        {c.shiftLabel && (
          <div>
            <dt>Смена</dt>
            <dd>{c.shiftLabel}</dd>
          </div>
        )}
        <div>
          <dt>Исполнители</dt>
          <dd>{c.assignees.length ? c.assignees.map((a) => a.name).join(', ') : 'не назначены'}</dd>
        </div>
        {c.equipmentName && (
          <div>
            <dt>Машина</dt>
            <dd>{c.equipmentName}</dd>
          </div>
        )}
        {c.areaId && (
          <div>
            <dt>Участок</dt>
            <dd>{areaPath(areas, c.areaId)}</dd>
          </div>
        )}
      </dl>

      {c.canEdit && (
        <>
          <div className="grid-half">
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy || i <= 0} onClick={() => void move(STAGES[i - 1]!)}>
              ← {i > 0 ? STAGE_LABEL[STAGES[i - 1]!] : 'назад'}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy || i >= STAGES.length - 1} onClick={() => void move(STAGES[i + 1]!)}>
              {i < STAGES.length - 1 ? STAGE_LABEL[STAGES[i + 1]!] : 'дальше'} →
            </button>
          </div>
          <button type="button" className="btn btn-primary btn-block" onClick={() => setAssign(true)}>
            <Icon name="users" size={18} />
            {c.assignees.length ? 'Изменить смену и людей' : 'Назначить на смену'}
          </button>
        </>
      )}

      <section className="stack" style={{ gap: 8 }}>
        <h2 className="section-title">Переналадка</h2>
        {p && p.changeover.length > 0 ? (
          <StepList items={p.changeover} manage={false} onOpen={(item, n) => setStep({ n, ...item })} />
        ) : (
          <p className="small muted" style={{ margin: 0 }}>
            Описание переналадки скоро появится.
          </p>
        )}
      </section>

      {p && <ProductFacts p={p} />}

      {step && p && (
        <Sheet title={`Переналадка · ${step.n + 1}`} onClose={() => setStep(null)}>
          <p className="step-title">{step.text}</p>
          <StepContent equipmentId="" details={step.details} fileIds={step.fileIds} files={p.files as EquipmentFileDto[]} />
        </Sheet>
      )}
      {assign && (
        <AssignSheet
          c={c}
          onClose={() => setAssign(false)}
          onDone={async (x) => {
            setC(x);
            setAssign(false);
            await onChanged();
          }}
        />
      )}
    </Sheet>
  );
}

type Person = { id: string; name: string; position: string | null; areaIds: string[] };

/** Назначить на смену: день, время (по умолчанию 09:00–18:00, можно дополнительные часы), люди, машина, количество. */
function AssignSheet({ c, onClose, onDone }: { c: ProdCardDto; onClose: () => void; onDone: (c: ProdCardDto) => Promise<void> }) {
  const { fail, toast } = useApp();
  const [day, setDay] = useState(localDate(Date.now(), FACTORY_TZ));
  const [from, setFrom] = useState('09:00');
  const [to, setTo] = useState('18:00');
  const [people, setPeople] = useState<Person[]>([]);
  const [equipment, setEquipment] = useState<EquipmentDto[]>([]);
  const [ids, setIds] = useState<string[]>(c.assignees.map((a) => a.id));
  const [eq, setEq] = useState(c.equipmentId ?? '');
  const [qty, setQty] = useState(c.qty !== null ? String(Math.max(0, c.qty - c.done)) : '');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    Promise.all([api.people(), api.equipment()])
      .then(([p, e]) => (setPeople(p), setEquipment(e)))
      .catch(fail);
  }, [fail]);
  async function save() {
    setBusy(true);
    try {
      const x = await api.assignCard(c.id, { day, from, to, assigneeIds: ids, equipmentId: eq || null, qty: qty.trim() ? Number(qty.replace(',', '.')) : null });
      platform.haptic('success');
      toast('Назначено — задача у сотрудника на панели');
      await onDone(x);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Sheet title="Назначить на смену" onClose={onClose}>
      <div className="field">
        <label htmlFor="asd">День</label>
        <input id="asd" className="input" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
      </div>
      <div className="grid-half">
        <div className="field">
          <label htmlFor="asf">С</label>
          <input id="asf" className="input" type="time" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="ast">До</label>
          <input id="ast" className="input" type="time" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>
      <span className="hint">Обычная смена 09:00–18:00. Дополнительные часы — просто поменяйте время.</span>
      <div className="field">
        <label htmlFor="asq">Количество на смену, шт.</label>
        <input id="asq" className="input" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="ase">Машина</label>
        <select id="ase" className="input" value={eq} onChange={(e) => setEq(e.target.value)}>
          <option value="">— не указана</option>
          {equipment.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <span className="label">Кто</span>
        <div className="stack" style={{ gap: 2, maxHeight: 240, overflowY: 'auto' }}>
          {people.map((p) => (
            <label key={p.id} className="check">
              <input type="checkbox" checked={ids.includes(p.id)} onChange={() => setIds((l) => (l.includes(p.id) ? l.filter((x) => x !== p.id) : [...l, p.id]))} />
              <span>
                {p.name}
                <span className="hint" style={{ display: 'block' }}>
                  {p.position ?? ''}
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>
      <button type="button" className="btn btn-primary btn-block" disabled={busy || !ids.length} onClick={() => void save()}>
        Назначить
      </button>
    </Sheet>
  );
}
